const inputClass =
  "w-full rounded-sm border border-gray-200 px-3.5 py-2.5 text-sm outline-none focus:border-gold focus:ring-2 focus:ring-gold/30";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-1 block text-[11px] font-bold uppercase tracking-[0.15em] text-navy/60">{label}</span>
    {children}
  </label>
);

export const EMPTY_PROPERTY = {
  title: "",
  location: "",
  location_zone: "Nagpur",
  property_type: "Residential",
  plot_size: "",
  size_category: "Medium",
  price_label: "Enquire for Price",
  price_value: "",
  description: "",
  location_advantage: "",
  amenitiesText: "",
  imagesText: "",
  status: "Available",
  featured: false,
  is_sample: false,
};

export function toForm(p) {
  return {
    ...EMPTY_PROPERTY,
    ...p,
    price_value: p.price_value ?? "",
    amenitiesText: (p.amenities || []).join(", "),
    imagesText: (p.images || []).join("\n"),
  };
}

export function toPayload(f) {
  return {
    title: f.title,
    location: f.location,
    location_zone: f.location_zone,
    property_type: f.property_type,
    plot_size: f.plot_size,
    size_category: f.size_category,
    price_label: f.price_label,
    price_value: f.price_value === "" ? null : Number(f.price_value),
    description: f.description,
    location_advantage: f.location_advantage,
    amenities: f.amenitiesText.split(",").map((s) => s.trim()).filter(Boolean),
    images: f.imagesText.split("\n").map((s) => s.trim()).filter(Boolean),
    status: f.status,
    featured: f.featured,
    is_sample: f.is_sample,
  };
}

export default function PropertyForm({ form, setForm, onSave, onCancel, saving }) {
  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  return (
    <form data-testid="property-form" onSubmit={(e) => { e.preventDefault(); onSave(); }} className="grid gap-4 rounded-xl border border-gray-200 bg-white p-6 sm:grid-cols-2">
      <Field label="Property Title *">
        <input data-testid="pf-title" required className={inputClass} value={form.title} onChange={set("title")} />
      </Field>
      <Field label="Location *">
        <input data-testid="pf-location" required className={inputClass} value={form.location} onChange={set("location")} placeholder="e.g. Near Samruddhi Mahamarg, Nagpur" />
      </Field>
      <Field label="Location Zone">
        <select data-testid="pf-zone" className={inputClass} value={form.location_zone} onChange={set("location_zone")}>
          {["Samruddhi Mahamarg", "Umred Road", "Wardha Road", "Nagpur", "Other Locations"].map((z) => (
            <option key={z}>{z}</option>
          ))}
        </select>
      </Field>
      <Field label="Property Type">
        <select data-testid="pf-type" className={inputClass} value={form.property_type} onChange={set("property_type")}>
          {["Residential", "Commercial", "Investment"].map((t) => (
            <option key={t}>{t}</option>
          ))}
        </select>
      </Field>
      <Field label="Plot Size">
        <input data-testid="pf-plot-size" className={inputClass} value={form.plot_size} onChange={set("plot_size")} placeholder="e.g. 1,000 - 1,500 sq.ft." />
      </Field>
      <Field label="Size Category">
        <select data-testid="pf-size-category" className={inputClass} value={form.size_category} onChange={set("size_category")}>
          {["Small", "Medium", "Large"].map((s) => (
            <option key={s}>{s}</option>
          ))}
        </select>
      </Field>
      <Field label="Price Label">
        <input data-testid="pf-price-label" className={inputClass} value={form.price_label} onChange={set("price_label")} />
      </Field>
      <Field label="Price Value (Lakhs, optional - used for budget filter)">
        <input data-testid="pf-price-value" type="number" min="0" step="0.5" className={inputClass} value={form.price_value} onChange={set("price_value")} />
      </Field>
      <Field label="Status">
        <select data-testid="pf-status" className={inputClass} value={form.status} onChange={set("status")}>
          {["Available", "Reserved", "Sold"].map((s) => (
            <option key={s}>{s}</option>
          ))}
        </select>
      </Field>
      <div className="flex items-end gap-6 pb-1">
        <label className="flex items-center gap-2 text-sm text-navy">
          <input data-testid="pf-featured" type="checkbox" checked={form.featured} onChange={set("featured")} className="h-4 w-4 accent-[#D4AF37]" />
          Featured
        </label>
        <label className="flex items-center gap-2 text-sm text-navy">
          <input data-testid="pf-is-sample" type="checkbox" checked={form.is_sample} onChange={set("is_sample")} className="h-4 w-4 accent-[#D4AF37]" />
          Sample listing
        </label>
      </div>
      <div className="sm:col-span-2">
        <Field label="Description">
          <textarea data-testid="pf-description" className={`${inputClass} min-h-[90px]`} value={form.description} onChange={set("description")} />
        </Field>
      </div>
      <div className="sm:col-span-2">
        <Field label="Location Advantage">
          <input data-testid="pf-advantage" className={inputClass} value={form.location_advantage} onChange={set("location_advantage")} />
        </Field>
      </div>
      <Field label="Amenities (comma separated)">
        <textarea data-testid="pf-amenities" className={`${inputClass} min-h-[70px]`} value={form.amenitiesText} onChange={set("amenitiesText")} placeholder="Internal roads, Water supply" />
      </Field>
      <Field label="Image URLs (one per line)">
        <textarea data-testid="pf-images" className={`${inputClass} min-h-[70px]`} value={form.imagesText} onChange={set("imagesText")} placeholder="https://..." />
      </Field>
      <div className="flex gap-3 sm:col-span-2">
        <button
          data-testid="pf-save-button"
          type="submit"
          disabled={saving}
          className="rounded-sm bg-gold px-6 py-3 text-sm font-bold uppercase tracking-wider text-navy transition-colors hover:bg-gold-light disabled:opacity-60"
        >
          {saving ? "Saving..." : "Save Property"}
        </button>
        <button
          data-testid="pf-cancel-button"
          type="button"
          onClick={onCancel}
          className="rounded-sm border border-navy/20 px-6 py-3 text-sm font-semibold text-navy transition-colors hover:border-navy"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
